import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Plus, Edit2, Trash2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import api, { formatErr } from "@/lib/api";
import { getSchema, pickPath } from "@/admin/resources/resource-schemas";
import { usePermissions } from "@/contexts/PermissionsContext";
import { moduleForPath, LEVEL_RANK } from "@/admin/modules";
import RelationalPicker from "@/admin/components/RelationalPicker";
import FileUpload from "@/admin/components/FileUpload";

function emptyDataFromSchema(schema) {
  const out = {};
  for (const f of schema.dataFields || []) {
    if (f.type === "checkbox") out[f.key] = false;
    else if (f.type === "csv") out[f.key] = "";
    else if (f.type === "multiref") out[f.key] = [];
    else if (f.type === "rows") out[f.key] = [];
    else if (f.type === "ref") out[f.key] = "";
    else if (f.type === "file") out[f.key] = "";
    else out[f.key] = "";
  }
  return out;
}

function dataToForm(data, schema) {
  const out = {};
  for (const f of schema.dataFields || []) {
    const v = data?.[f.key];
    if (f.type === "csv") out[f.key] = Array.isArray(v) ? v.join(",") : (v || "");
    else if (f.type === "multiref") out[f.key] = Array.isArray(v) ? v : [];
    else if (f.type === "rows") out[f.key] = Array.isArray(v) ? v : [];
    else if (f.type === "json") out[f.key] = v == null ? "" : (typeof v === "string" ? v : JSON.stringify(v, null, 2));
    else if (f.type === "checkbox") out[f.key] = !!v;
    else if (v == null) out[f.key] = "";
    else out[f.key] = v;
  }
  return out;
}

function formToData(form, schema) {
  const out = {};
  for (const f of schema.dataFields || []) {
    const raw = form[f.key];
    if (f.type === "multiref") { if (Array.isArray(raw) && raw.length) out[f.key] = raw; continue; }
    if (f.type === "rows") {
      const arr = Array.isArray(raw) ? raw : [];
      const cleaned = arr.map((r) => {
        const o = {};
        for (const c of (f.columns || [])) {
          let cv = r?.[c.key];
          if (cv === "" || cv == null) continue;
          if (c.type === "number") cv = isNaN(Number(cv)) ? cv : Number(cv);
          o[c.key] = cv;
        }
        return o;
      }).filter((o) => (f.requiredKey ? o[f.requiredKey] : Object.keys(o).length));
      if (cleaned.length) out[f.key] = cleaned;
      continue;
    }
    if (raw === "" || raw == null) continue;
    if (f.type === "csv") out[f.key] = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
    else if (f.type === "number") out[f.key] = isNaN(Number(raw)) ? raw : Number(raw);
    else if (f.type === "json") { try { out[f.key] = JSON.parse(raw); } catch { out[f.key] = raw; } }
    else out[f.key] = raw;
  }
  return out;
}

function renderCellValue(v) {
  if (v == null || v === "") return <span className="text-muted-foreground/60">—</span>;
  if (typeof v === "boolean") return v ? <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/15">Yes</Badge> : <span className="text-muted-foreground">No</span>;
  if (Array.isArray(v)) {
    if (v.length && typeof v[0] === "object") return <span className="text-xs">{v.length} {v.length === 1 ? "entry" : "entries"}</span>;
    return <span className="text-xs">{v.slice(0, 4).join(", ")}{v.length > 4 ? ` +${v.length - 4}` : ""}</span>;
  }
  if (typeof v === "object") return <code className="text-[10px] text-muted-foreground">{Object.keys(v).slice(0, 3).join(",")}</code>;
  if (typeof v === "string" && v.length > 40) return <span title={v}>{v.slice(0, 40)}…</span>;
  return String(v);
}

export default function ResourceCRUDPage({ kind: kindProp, liveStatus = null, banner = null }) {
  const params = useParams();
  const location = useLocation();
  const kind = kindProp || params.kind;
  const schema = useMemo(() => getSchema(kind), [kind]);

  const { levelFor } = usePermissions();
  const level = levelFor(moduleForPath(location.pathname) || location.pathname);
  const canWrite = LEVEL_RANK[level] >= LEVEL_RANK.edit;
  const canDelete = LEVEL_RANK[level] >= LEVEL_RANK.full;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | row
  const [form, setForm] = useState({ name: "", description: "", is_active: true, data: {} });
  const [dataForm, setDataForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [liveData, setLiveData] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/admin/resources/${kind}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [kind]);

  // ---- Optional live-status polling (for SIP Trunks / Extensions etc.) ----
  useEffect(() => {
    if (!liveStatus?.endpoint) return;
    let alive = true;
    async function tick() {
      try {
        const r = await api.get(liveStatus.endpoint);
        if (alive) setLiveData(r.data);
      } catch (_) { /* keep last good */ }
    }
    tick();
    const id = setInterval(tick, liveStatus.intervalMs || 5000);
    return () => { alive = false; clearInterval(id); };
  }, [liveStatus?.endpoint, liveStatus?.intervalMs]);

  function startNew() {
    setForm({ name: "", description: "", is_active: true });
    setDataForm(emptyDataFromSchema(schema));
    setEditing("new");
  }
  function startEdit(row) {
    setForm({ name: row.name || "", description: row.description || "", is_active: !!row.is_active });
    setDataForm(dataToForm(row.data || {}, schema));
    setEditing(row);
  }
  function close() { setEditing(null); }

  async function save() {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || "",
        is_active: !!form.is_active,
        data: formToData(dataForm, schema),
      };
      if (editing === "new") {
        await api.post(`/admin/resources/${kind}`, payload);
        toast.success(`${schema.title.replace(/s$/, "")} created`);
      } else {
        await api.patch(`/admin/resources/${kind}/${editing.id}`, payload);
        toast.success("Saved");
      }
      close();
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  }

  async function remove(row) {
    try {
      await api.delete(`/admin/resources/${kind}/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  }

  async function toggleActive(row) {
    try {
      await api.patch(`/admin/resources/${kind}/${row.id}`, { is_active: !row.is_active });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  }

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => (r.name || "").toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q));
  }, [rows, search]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid={`admin-page-title-${kind}`}>{schema.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{schema.subtitle}</p>
        </div>
        <Button data-testid={`add-${kind}-btn`} onClick={startNew} disabled={!canWrite} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5" style={{ display: canWrite ? undefined : "none" }}>
          <Plus className="w-4 h-4" /> Add new
        </Button>
      </div>

      <div className="mb-4 relative max-w-sm">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid={`search-${kind}-input`}
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 h-9"
        />
      </div>

      <Card className="overflow-hidden">
        {/* Optional banner (e.g. ARI unreachable warning) */}
        {banner ? <div className="border-b border-border">{banner(liveData)}</div> : null}
        {loading ? (
          <div className="py-16 text-center text-sm text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <div className="text-sm text-muted-foreground">No entries yet.</div>
            {canWrite && (
              <Button data-testid={`empty-add-${kind}-btn`} onClick={startNew} variant="outline" className="mt-3 gap-1.5">
                <Plus className="w-4 h-4" /> Add your first
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr className="text-left">
                  <th className="px-4 py-2 font-semibold">Name</th>
                  {schema.columns?.map((c) => (
                    <th key={c.key} className="px-4 py-2 font-semibold">{c.label}</th>
                  ))}
                  {liveStatus?.columns?.map((c) => (
                    <th key={`live-${c.key}`} className="px-4 py-2 font-semibold">{c.label}</th>
                  ))}
                  <th className="px-4 py-2 font-semibold">Active</th>
                  <th className="px-4 py-2 font-semibold w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const liveRow = liveStatus?.extractRowState ? liveStatus.extractRowState(liveData, r) : null;
                  return (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-${kind}-${r.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{r.name}</div>
                      {r.description && <div className="text-xs text-muted-foreground">{r.description}</div>}
                    </td>
                    {schema.columns?.map((c) => (
                      <td key={c.key} className="px-4 py-2.5">{renderCellValue(pickPath(r, c.key))}</td>
                    ))}
                    {liveStatus?.columns?.map((c) => (
                      <td key={`live-${c.key}`} className="px-4 py-2.5" data-testid={`live-${c.key}-${r.id}`}>
                        {c.render ? c.render(liveRow, r) : renderCellValue(liveRow?.[c.key])}
                      </td>
                    ))}
                    <td className="px-4 py-2.5">
                      <Switch
                        data-testid={`active-toggle-${r.id}`}
                        checked={!!r.is_active}
                        disabled={!canWrite}
                        onCheckedChange={() => toggleActive(r)}
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        {canWrite && (
                          <Button data-testid={`edit-${r.id}`} onClick={() => startEdit(r)} variant="ghost" size="icon" className="h-8 w-8">
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button data-testid={`delete-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{r.name}"?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction data-testid={`confirm-delete-${r.id}`} className="bg-destructive hover:bg-destructive/90" onClick={() => remove(r)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        )}
                        {!canWrite && !canDelete && <span className="text-xs text-muted-foreground/60">View only</span>}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Edit/Create dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? `New ${schema.title.replace(/s$/, "")}` : `Edit ${editing?.name || ""}`}</DialogTitle>
            <DialogDescription>{schema.subtitle}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Name <span className="text-destructive">*</span></Label>
                <Input data-testid="resource-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label>Description</Label>
                <Input data-testid="resource-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="flex items-center gap-2 sm:col-span-2">
                <Switch data-testid="resource-active-switch" checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: !!v })} />
                <Label>Active</Label>
              </div>
            </div>

            {schema.dataFields?.length > 0 && (
              <div className="border-t pt-4">
                <div className="text-overline text-muted-foreground mb-3">Configuration</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {schema.dataFields
                    .filter((f) => !f.showIf || f.showIf(dataForm))
                    .map((f) => (
                    <FieldInput key={f.key} field={f} form={dataForm} value={dataForm[f.key]} onChange={(v) => setDataForm({ ...dataForm, [f.key]: v })} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button data-testid="resource-cancel-btn" variant="outline" onClick={close}>Cancel</Button>
            <Button data-testid="resource-save-btn" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldInput({ field, value, onChange, form = {} }) {
  const id = `field-${field.key}`;
  const HelpText = field.help ? (
    <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{field.help}</p>
  ) : null;
  if (field.type === "checkbox") {
    return (
      <div className="flex items-start gap-2 sm:col-span-2">
        <Switch id={id} data-testid={id} checked={!!value} onCheckedChange={(v) => onChange(!!v)} />
        <div>
          <Label htmlFor={id}>{field.label}</Label>
          {HelpText}
        </div>
      </div>
    );
  }
  if (field.type === "select") {
    const opts = (field.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
    return (
      <div>
        <Label htmlFor={id}>{field.label}</Label>
        <Select value={String(value ?? "")} onValueChange={(v) => onChange(v)}>
          <SelectTrigger data-testid={id} className="h-9">
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {opts.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {HelpText}
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="sm:col-span-2">
        <Label htmlFor={id}>{field.label}</Label>
        <Textarea id={id} data-testid={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} rows={4} />
        {HelpText}
      </div>
    );
  }
  if (field.type === "json") {
    return (
      <div className="sm:col-span-2">
        <Label htmlFor={id}>{field.label}</Label>
        <Textarea id={id} data-testid={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder='{ "key": "value" }' rows={4} className="font-mono text-xs" />
        {HelpText}
      </div>
    );
  }
  if (field.type === "ref" || field.type === "multiref") {
    const refKind = field.refKindFrom ? field.refKindFrom(form) : field.refKind;
    if (!refKind) {
      return (
        <div className="sm:col-span-2">
          <Label>{field.label}</Label>
          <p className="text-xs text-muted-foreground mt-1">Pick a route kind above to choose a target.</p>
        </div>
      );
    }
    return (
      <div className="sm:col-span-2">
        <Label>{field.label}</Label>
        <RelationalPicker
          key={refKind}
          kind={refKind}
          value={value}
          onChange={onChange}
          multi={field.type === "multiref"}
          placeholder="Select…"
          testId={id}
          labelKey={field.refLabelKey || "name"}
          returnKey={field.returnKey || "id"}
        />
        {HelpText}
      </div>
    );
  }
  if (field.type === "file") {
    return (
      <div className="sm:col-span-2">
        <Label>{field.label}</Label>
        <FileUpload kind={field.uploadKind} value={value || ""} onChange={onChange} accept={field.accept || "audio/*"} testId={id} />
        {HelpText}
      </div>
    );
  }
  if (field.type === "rows") {
    const rows = Array.isArray(value) ? value : [];
    const cols = field.columns || [];
    const update = (i, k, v) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
    const addRow = () => onChange([...rows, {}]);
    const delRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
    return (
      <div className="sm:col-span-2">
        <Label>{field.label}</Label>
        <div className="space-y-2 mt-1.5">
          {rows.length === 0 && <p className="text-xs text-muted-foreground">No entries yet — add one below.</p>}
          {rows.map((r, i) => (
            <div key={i} className="flex items-end gap-2">
              {cols.map((c) => (
                <div key={c.key} className="flex-1 min-w-0">
                  <span className="text-[10px] text-muted-foreground block mb-0.5">{c.label}</span>
                  <Input
                    data-testid={`${id}-${i}-${c.key}`}
                    type={c.type === "number" ? "number" : "text"}
                    value={r?.[c.key] ?? ""}
                    placeholder={c.placeholder}
                    onChange={(e) => update(i, c.key, e.target.value)}
                    className="h-8"
                  />
                </div>
              ))}
              <Button type="button" data-testid={`${id}-del-${i}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => delRow(i)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button type="button" data-testid={`${id}-add`} variant="outline" size="sm" className="gap-1.5" onClick={addRow}>
            <Plus className="w-3.5 h-3.5" /> Add row
          </Button>
        </div>
        {HelpText}
      </div>
    );
  }
  if (field.type === "time") {
    return (
      <div>
        <Label htmlFor={id}>{field.label}</Label>
        <Input id={id} data-testid={id} type="time" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
        {HelpText}
      </div>
    );
  }
  return (
    <div>
      <Label htmlFor={id}>{field.label}</Label>
      <Input
        id={id}
        data-testid={id}
        type={field.type === "number" ? "number" : "text"}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
      />
      {HelpText}
    </div>
  );
}
